import { SearchInput } from "@/components/flexkit/SearchInput";
import type { TableSortOrder } from "@/components/flexkit/tables/SortableTable";
import { toApiSortOrder } from "@/components/flexkit/tables/sorting-utils";
import { MembersList, type SortColumn } from "@/components/settings/organization/members/MembersList";
import { MemberRoleFilter } from "@/components/settings/organization/members/MemberRoleFilter";
import { MemberStatusFilter } from "@/components/settings/organization/members/MemberStatusFilter";
import {
    RoleFilterValue,
    roleFilterValueToApiRoles,
    StatusFilterValue,
    statusFilterValueToApiStatuses,
} from "@/components/settings/organization/members/members-utils";
import { useMembership } from "@/hooks/use-membership";
import { useMembers } from "@/queries/organization-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import type { ListMembersRequest_SortField } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useState, type FC } from "react";
import { useDebounce } from "use-debounce";

export const MembersTab: FC = () => {
    const { data: user } = useAuthenticatedUser();
    const { membership } = useMembership();
    const [searchQuery, setSearchQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState<RoleFilterValue>(RoleFilterValue.All);
    const [statusFilter, setStatusFilter] = useState<StatusFilterValue>(StatusFilterValue.All);

    const [sortColumn, setSortColumn] = useState<SortColumn | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>(undefined);

    const [debouncedSearchQuery] = useDebounce(searchQuery, 250, { trailing: true });

    const apiRoles = roleFilterValueToApiRoles(roleFilter);
    const apiStatuses = statusFilterValueToApiStatuses(statusFilter);

    const { data: membersData, isFetching: isMembersLoading } = useMembers({
        filter: {
            search: debouncedSearchQuery,
            roles: apiRoles,
            statuses: apiStatuses,
        },
        sort: {
            field: sortColumn,
            order: toApiSortOrder(sortOrder),
        },
    });

    const handleSort = useCallback((columnName: ListMembersRequest_SortField, order: TableSortOrder) => {
        setSortColumn(columnName);
        setSortOrder(order);
    }, []);

    const handleResetFilters = useCallback(() => {
        setSearchQuery("");
        setRoleFilter(RoleFilterValue.All);
        setStatusFilter(StatusFilterValue.All);
    }, []);

    return (
        <>
            <div className="flex flex-col gap-2 @container @lg:flex-row @lg:items-center">
                <SearchInput
                    wrapperClassName="flex-1"
                    className="max-w-none @sm:min-w-[20ch]"
                    placeholder="Search by name, email or ID"
                    value={searchQuery || ""}
                    onChange={(e) => setSearchQuery(e.target.value.trimStart().replace(/\s+/g, " "))}
                    onClear={() => setSearchQuery("")}
                    isLoading={isMembersLoading}
                    data-tracking-id="search-members-input"
                />
                <div className="flex flex-col items-stretch gap-2 @sm:flex-row">
                    <MemberRoleFilter value={roleFilter} onChange={setRoleFilter} />
                    <MemberStatusFilter
                        value={statusFilter}
                        onChange={setStatusFilter}
                        showCount={roleFilter === RoleFilterValue.All && debouncedSearchQuery.trim().length === 0}
                    />
                </div>
            </div>
            <MembersList
                user={user}
                membership={membership}
                data={{
                    members: membersData?.members || [],
                }}
                isLoading={isMembersLoading}
                hasSearchQuery={debouncedSearchQuery.trim().length > 0}
                roleFilter={roleFilter}
                statusFilter={statusFilter}
                onResetFilters={handleResetFilters}
                setSort={handleSort}
                sort={{
                    sortBy: sortColumn,
                    sortOrder,
                }}
            />
        </>
    );
};
